// store/useSessionStore.ts
import { getBrands } from "api/brand.service";
import { getCategories } from "api/category.service";
import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import { getBackOrders } from "api/order.service";
import { getProductSummaries } from "api/product.service";
import { getStorageKey, MAX_PAGE_SIZE } from "common/constants";
import { safeLog } from "common/utils";
import {
  Brands,
  Categories,
  ProductQuickSearches,
  ProductResults,
  ProductSummaries,
  ProductSummary,
} from "types/model.types";
import { mergeProducts, mergeProductSummaries } from "utils/helper";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

interface ProductStore {
  sessionStart: Date;
  isLoading: boolean;
  productSummaries: ProductResults;
  allProducts: ProductResults;
  categories: Categories;
  selectedCategory: string | undefined;
  brands: Brands;
  selectedBrand: string | undefined;
  activePage: number;
  activeProductQuickSearchData: ProductQuickSearches;

  forceRefresh: () => Promise<void>;
  setSelectedCategory: (cat: string | undefined) => void;
  setSelectedBrand: (brand: string | undefined) => void;
  setActivePage: (page: number) => void;
  setProductSummaries: (items: ProductResults) => void;

  getFeatured: () => ProductSummaries;
  getTopSelling: () => ProductSummaries;

  fetchBrands: () => Promise<void>;
  fetchCategories: () => Promise<void>;

  // handles initial hydration of product records
  fetchInitialProducts: () => Promise<void>;
  // handles background sync every 5 minutes any any updated product from admin
  syncModifiedProducts: () => Promise<void>;
  syncLocalProductChanges: (updated: ProductSummary) => Promise<void>;
  syncBackorders: () => Promise<void>;

  getActiveProductQuickSearchData: () => ProductQuickSearches;
}

// Coerce anything that isn't a well-formed ProductResults (e.g. an empty-string
// or HTML 200 body, or stale persisted state) to an empty result, so a bad
// response can't get persisted and crash every subsequent page load.
const toProductResults = (value: any): ProductResults =>
  value && Array.isArray(value.items)
    ? value
    : { items: [], totalRows: 0 };

export const markBackorderedProducts = (
  products: ProductSummaries,
  backorders: { productCode: string }[],
): ProductSummaries => {
  const backorderCodes = new Set(backorders.map((b) => b.productCode));

  return products.map((product) => ({
    ...product,
    backorder: backorderCodes.has(product.productCode),
  }));
};

export const useProductStore = create<ProductStore>()(
  persist(
    (set, get) => ({
      sessionStart: new Date(),
      activeProductQuickSearchData: [],

      isLoading: false,
      activePage: 1,

      productSummaries: { items: [], totalRows: 0 },
      allProducts: { items: [], totalRows: 0 },
      categories: [],
      brands: [],
      selectedCategory: undefined,
      selectedBrand: undefined,

      forceRefresh: async () => {
        // Clear immediately so UI resets
        set({
          allProducts: { items: [], totalRows: 0 },
          activeProductQuickSearchData: [],
          isLoading: true,
          sessionStart: new Date(),
        });

        try {
          const response = await safeApiCall(
            getProductSummaries("all", 1, 5000, null),
          );

          const data = toProductResults(response?.data);

          // Build the stable quick-search array ONCE
          const quickSearch = Array.from(
            new Map(
              data.items.map((p) => [
                p.productCode,
                {
                  id: p.productCode,
                  title: p.title,
                  codes: `${p.productCode} ${p.supplierCodes || ""} ${p.altCode || ""}`,
                },
              ]),
            ).values(),
          );

          set({
            allProducts: data,
            activeProductQuickSearchData: quickSearch,
            isLoading: false,
          });
        } catch (error) {
          logError(error);
          set({ isLoading: false });
        }
      },

      // Setters for filters
      setSelectedCategory: (cat) => set({ selectedCategory: cat }),
      // Setters for filters
      setSelectedBrand: (brand) => set({ selectedBrand: brand }),
      // Setters for filters
      setActivePage: (page) => set({ activePage: page }),
      // Setters for filters
      setProductSummaries: (items) =>
        set({ productSummaries: toProductResults(items) }),

      /** auto fetch via useEffect on affected page
       * useProductStore.getState().fetchBrands();
       * useProductStore.getState().fetchCategories()
       * */
      // there's only < 20 items - no need to check the isLoading/isFetched
      fetchBrands: async () => {
        const res = await safeApiCall(getBrands());
        set({ brands: res?.data ?? [] });
      },
      // there's only < 20 items - no need to chec the isLoading/isFetched
      fetchCategories: async () => {
        const res = await safeApiCall(getCategories());
        set({ categories: res?.data ?? [] });
      },
      // Fetch all products initially
      // This will be called in useEffect in App.tsx
      fetchInitialProducts: async () => {
        if (get().isLoading) return;
        set({ isLoading: true });

        try {
          // Set the fetched products to the store
          //(page size), and no modifiedAfter filter
          const response = await safeApiCall(
            getProductSummaries("all", 1, 5000, null),
          ); // get all, starting from page 1, 5000 items
          const data = toProductResults(response?.data);
          // Build the stable quick-search array ONCE
          const quickSearch = Array.from(
            new Map(
              data.items.map((p) => [
                p.productCode,
                {
                  id: p.productCode,
                  title: p.title,
                  codes: `${p.productCode} ${p.supplierCodes || ""} ${p.altCode || ""}`,
                },
              ]),
            ).values(),
          );

          set({
            allProducts: data,
            activeProductQuickSearchData: quickSearch,
          });
        } catch (error) {
          logError(error);
        } finally {
          set({ isLoading: false });
        }
      },
      syncLocalProductChanges: async (updated: ProductSummary) => {
        set((state) => {
          const updatedItems = state.allProducts.items.map((p) =>
            p.id === updated.id ? { ...p, ...updated } : p,
          );

          return {
            allProducts: {
              ...state.allProducts,
              items: updatedItems,
            },
            sessionStart: new Date(), // 🔁 Reset timestamp
          };
        });
      },

      // This will fetch all products and update the store with the modified products
      // It will also reset the sessionStart to the current time
      syncModifiedProducts: async () => {
        const state = get();
        // --- SAFETY CHECK 1: sessionStart must exist and be a Date ---
        if (!state.sessionStart || !(state.sessionStart instanceof Date)) {
          safeLog("syncModifiedProducts: sessionStart not ready");
          return;
        }

        const sessionTime = get().sessionStart.toISOString();
        try {
          const response = await safeApiCall(
            getProductSummaries("all", 1, MAX_PAGE_SIZE, sessionTime),
          ); // get all, starting from page 1, 5000 items (page size), and no modifiedAfter filter
          //const data: ProductResults = await res.data;
          const data: ProductResults = (await response?.data) ?? {
            totalRows: 0,
            items: [],
          };
          const updates: ProductSummaries = data.items ?? [];
          if (updates.length === 0) {
            safeLog(
              "ProductStore: (sync) - No updates available.",
              "will check again soon.",
            );
            return;
          }
          // --- SAFETY CHECK 2: allProducts must exist and have items ---
          if (!state.allProducts || !Array.isArray(state.allProducts.items)) {
            safeLog("syncModifiedProducts: allProducts not ready");
            return;
          }

          // --- SAFETY CHECK 3: productSummaries must exist and have items ---
          if (
            !state.productSummaries ||
            !Array.isArray(state.productSummaries.items)
          ) {
            safeLog("syncModifiedProducts: productSummaries not ready");
            return;
          }

          // Merge products
          const merged = [...state.allProducts.items];
          const featureMerged = [...state.productSummaries.items];

          set({
            allProducts: {
              ...state.allProducts,
              items: mergeProducts(merged, updates),
            },
            productSummaries: {
              ...state.productSummaries,
              items: mergeProductSummaries(featureMerged, updates),
            },
            sessionStart: new Date(),
          });
        } catch (error) {
          logError(error);
        }
      },
      syncBackorders: async () => {
        try {
          const response = await safeApiCall(getBackOrders());
          const backorders = Array.isArray(response?.data) ? response.data : [];
          const allProducts = toProductResults(get().allProducts);
          const productSummaries = toProductResults(get().productSummaries);

          // Update the allProducts with backorder info
          // This assumes that the backorder status is relevant to the allProducts as well
          const updatedAll = {
            ...allProducts,
            items: markBackorderedProducts(allProducts.items, backorders),
          };

          // Update the summaries with backorder info
          // This assumes that the backorder status is relevant to the summaries as well
          const updatedSummaries = {
            ...productSummaries,
            items: markBackorderedProducts(productSummaries.items, backorders),
          };
          // Update the store with the new backorder info
          set({
            allProducts: updatedAll,
            productSummaries: updatedSummaries,
          });
        } catch (error) {
          logError(error);
        }
      },
      getFeatured: () =>
        toProductResults(get().productSummaries).items.filter(
          (p) => Boolean(p.featured) === true,
        ),

      getTopSelling: () =>
        toProductResults(get().productSummaries).items.filter(
          (p) => Boolean(p.topSelling) === true,
        ),

      getActiveProductQuickSearchData: () => get().activeProductQuickSearchData,
    }),
    {
      name: `${getStorageKey("useProductStore")}`, // localStorage key
      storage: createJSONStorage(() => localStorage),
      //Zustand’s persist() accepts a deserialize option through merge, which lets us rehydrate the sessionStart field properly
      merge: (persisted, current) => {
        const restored = {
          ...current,
          ...(persisted as ProductStore),
        };

        // 🔁 Convert sessionStart back to a Date object
        if (
          persisted &&
          typeof persisted === "object" &&
          "sessionStart" in persisted
        ) {
          restored.sessionStart = new Date((persisted as any).sessionStart);
        }

        // Discard malformed persisted product data rather than crash on it
        restored.productSummaries = toProductResults(restored.productSummaries);
        restored.allProducts = toProductResults(restored.allProducts);

        return restored;
      },
    },
  ),
);
